/**
 * Scenario cleanup + drift detection helpers.
 *
 * Day 73 P4.1 C.9 + BACKLOG L427:
 *   - filterScenarioToCurrentKeys — drop stale keys from a scenario
 *     JSON when current valid-key lists differ from the persisted
 *     shape (e.g. user-pref schema change, code rename).
 *   - scenarioMatchesState — drift detection comparing current
 *     filters/weights/constraints to a scenario's saved values,
 *     with default-vs-explicit normalization per InlineInput's
 *     isFromDefault logic (ParamsSidebar.tsx:56-58).
 *
 * Pure functions — no React, no Supabase, no toast. All UI surface
 * lives at the call site.
 */

import type { Scenario } from '../hooks/useScenarios';

// Mirrors ParamsSidebar WeightState shape (kept local to avoid
// circular type imports through the component layer).
export interface WeightState {
  enabled: boolean;
  weight: string;
}

export type ScenarioFilters = Scenario['filters'];
export type ScenarioWeights = Scenario['weights'];
export type ScenarioConstraints = Scenario['constraints'];

export interface ValidKeySets {
  filterKeys: Set<string>;
  weightKeys: Set<string>;
  constraintKeys: Set<string>;
}

export interface CleanupResult {
  cleaned: {
    filters: ScenarioFilters;
    weights: ScenarioWeights;
    constraints: ScenarioConstraints;
  };
  dropped: {
    filterKeys: string[];
    weightKeys: string[];
    constraintKeys: string[];
  };
}

/**
 * Filter a scenario's three JSONB shapes to only the keys currently
 * present in the valid key sets. Returns both the cleaned shapes and
 * the dropped keys (for surfacing a toast on the apply path).
 */
export function filterScenarioToCurrentKeys(
  scenario: Pick<Scenario, 'filters' | 'weights' | 'constraints'>,
  validKeys: ValidKeySets
): CleanupResult {
  const droppedFilterKeys: string[] = [];
  const droppedWeightKeys: string[] = [];
  const droppedConstraintKeys: string[] = [];

  const cleanedFilters: ScenarioFilters = {};
  for (const [k, v] of Object.entries(scenario.filters ?? {})) {
    if (validKeys.filterKeys.has(k)) cleanedFilters[k] = v;
    else droppedFilterKeys.push(k);
  }

  const cleanedWeights: ScenarioWeights = {};
  for (const [k, v] of Object.entries(scenario.weights ?? {})) {
    if (validKeys.weightKeys.has(k)) cleanedWeights[k] = v;
    else droppedWeightKeys.push(k);
  }

  const cleanedConstraints: ScenarioConstraints = {};
  for (const [k, v] of Object.entries(scenario.constraints ?? {})) {
    if (validKeys.constraintKeys.has(k)) cleanedConstraints[k] = v;
    else droppedConstraintKeys.push(k);
  }

  return {
    cleaned: {
      filters: cleanedFilters,
      weights: cleanedWeights,
      constraints: cleanedConstraints,
    },
    dropped: {
      filterKeys: droppedFilterKeys,
      weightKeys: droppedWeightKeys,
      constraintKeys: droppedConstraintKeys,
    },
  };
}

/**
 * Compare two filter/constraint string values with default-vs-explicit
 * normalization. Treats absence ('' or undefined) and the literal
 * default value as equivalent — mirrors InlineInput.isFromDefault
 * (ParamsSidebar.tsx:56-58) and ArbiToggle boolean serialization
 * ('true' for ON, '' for OFF; defensive: also coerce 'false' to '').
 */
export function normalizedEqual(
  a: string | undefined,
  b: string | undefined,
  defaultValue: string | undefined
): boolean {
  const resolve = (v: string | undefined): string | undefined => {
    if (v === 'false') return defaultValue ?? '';
    if (v === '' || v === undefined) return defaultValue ?? '';
    return v;
  };
  return resolve(a) === resolve(b);
}

/**
 * Weight equality — both disabled = equal regardless of weight string;
 * both enabled = compare weight strings strict; mismatched enabled
 * states = not equal.
 */
export function weightEqual(a: WeightState | undefined, b: WeightState | undefined): boolean {
  const aEnabled = a?.enabled ?? false;
  const bEnabled = b?.enabled ?? false;
  if (aEnabled !== bEnabled) return false;
  if (!aEnabled) return true;
  return (a?.weight ?? '') === (b?.weight ?? '');
}

/**
 * Full drift comparison: does the current state match the scenario's
 * saved values (after normalization)?
 *
 * Defaults map is supplied by the caller — typically the same
 * computeParamDefaults result the UI uses for InlineInput display.
 * Per Day 73 Decision 4.4b, drift uses the SAME prefs-overridden
 * defaults the user sees in the UI, so "typing the displayed default"
 * counts as match, not drift.
 */
export function scenarioMatchesState(
  scenario: Pick<Scenario, 'filters' | 'weights' | 'constraints'>,
  state: {
    filters: ScenarioFilters;
    weights: ScenarioWeights;
    constraints: ScenarioConstraints;
  },
  defaults: Record<string, string>,
  validKeys: ValidKeySets
): boolean {
  // Compare across the UNION of keys present in scenario or state,
  // but ONLY for keys currently valid. Stale keys in scenario are
  // ignored (they'd have been dropped on apply via
  // filterScenarioToCurrentKeys); stale keys in state shouldn't
  // exist post-apply but defensive ignore is safe.
  const filterUnion = new Set<string>([
    ...Object.keys(scenario.filters ?? {}),
    ...Object.keys(state.filters ?? {}),
  ]);
  for (const k of filterUnion) {
    if (!validKeys.filterKeys.has(k)) continue;
    if (!normalizedEqual(state.filters?.[k], scenario.filters?.[k], defaults[k])) {
      return false;
    }
  }

  const constraintUnion = new Set<string>([
    ...Object.keys(scenario.constraints ?? {}),
    ...Object.keys(state.constraints ?? {}),
  ]);
  for (const k of constraintUnion) {
    if (!validKeys.constraintKeys.has(k)) continue;
    if (!normalizedEqual(state.constraints?.[k], scenario.constraints?.[k], defaults[k])) {
      return false;
    }
  }

  const weightUnion = new Set<string>([
    ...Object.keys(scenario.weights ?? {}),
    ...Object.keys(state.weights ?? {}),
  ]);
  for (const k of weightUnion) {
    if (!validKeys.weightKeys.has(k)) continue;
    if (!weightEqual(state.weights?.[k], scenario.weights?.[k])) {
      return false;
    }
  }

  return true;
}
